import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { extractErrorMessage } from "@/core/api/errors";
import { RUN_POLL_INTERVAL_MS } from "./constants";
import { invalidateReportViews, reportKey } from "./query";
import type {
  NewReportRequest,
  ProfitabilityReport,
  ReportBuild,
  ReportDates,
} from "./types";
import type { ReportVersion } from "./version-types";

const IDLE_REPORT_POLL_MS = 15_000;

export const reportsPath = (businessId: string | null, reportId?: string) =>
  `/businesses/${businessId}/profitability_reports${reportId ? `/${reportId}` : ""}`;

export const useReports = (
  businessId: string | null,
  workflowVersion: 1 | 2 = 2,
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...reportKey(businessId), "reports", workflowVersion],
    queryFn: ({ signal }) =>
      api
        .get<{ reports: ProfitabilityReport[] }>(reportsPath(businessId), {
          params: { workflow_version: workflowVersion },
          signal,
        })
        .then((res) => res.data.reports),
    enabled: !!businessId,
    // Read again while a build runs, or a draft's sample run (a version
    // brought back starts one), so Publish opens when it is done.
    refetchInterval: (query) =>
      query.state.data?.some(
        (report) =>
          report.build_status === "building" ||
          report.draft_sample_run_status === "pending" ||
          report.draft_sample_run_status === "running",
      )
        ? RUN_POLL_INTERVAL_MS
        : false,
    retry: false,
  });
};

/**
 * One template by id, archived or not: its own page must open an archived
 * template (to say so and offer Restore), which the active list leaves out.
 * Null when the template does not exist or is another business's.
 */
export const useReport = (
  businessId: string | null,
  reportId: string,
  enabled = true,
  /**
   * Keep reading it while idle too, and on every return to the page: a build
   * started elsewhere (another tab, another manager, Rebuild) shows up here
   * rather than when the page is next opened.
   */
  watch = false,
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...reportKey(businessId), "reports", "one", 2, reportId],
    queryFn: ({ signal }) =>
      api
        .get<{
          report: ProfitabilityReport;
        }>(reportsPath(businessId, reportId), {
          params: { workflow_version: 2 },
          signal,
        })
        .then((res) => res.data.report)
        .catch((error: unknown) => {
          if (extractErrorMessage(error).status === 404) return null;
          throw error;
        }),
    enabled: !!businessId && enabled,
    refetchInterval: (query) =>
      query.state.data?.build_status === "building"
        ? RUN_POLL_INTERVAL_MS
        : watch && IDLE_REPORT_POLL_MS,
    refetchOnWindowFocus: watch ? "always" : undefined,
    retry: false,
  });
};

/**
 * A template's builds, newest first, re-read while one is running. Kept under
 * the reports key, so whatever refreshes the templates refreshes it too.
 */
export const useReportBuilds = (
  businessId: string | null,
  reportId: string | null,
  /** The template's build status: a change (a build started or ended) reads the list again. */
  buildStatus: string | null,
) => {
  const api = useApi();
  return useQuery<ReportBuild[]>({
    queryKey: [
      ...reportKey(businessId),
      "reports",
      reportId,
      "builds",
      buildStatus,
    ],
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      api
        .get<{
          builds: ReportBuild[];
        }>(`${reportsPath(businessId, reportId ?? undefined)}/builds`, {
          signal,
        })
        .then((res) => res.data.builds),
    enabled: !!businessId && !!reportId,
    refetchInterval: (query) =>
      query.state.data?.some((build) => build.status === "building")
        ? RUN_POLL_INTERVAL_MS
        : false,
    retry: false,
  });
};

const invalidateReports = (
  client: ReturnType<typeof useQueryClient>,
  businessId: string | null,
) => {
  void client.invalidateQueries({
    queryKey: [...reportKey(businessId), "reports"],
  });
  void client.invalidateQueries({ queryKey: ["business"] });
};

export const useCreateReportDefinition = (businessId: string | null) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: NewReportRequest) =>
      api
        .post<{ report: ProfitabilityReport }>(reportsPath(businessId), request)
        .then((res) => res.data.report),
    onSuccess: () => invalidateReports(client, businessId),
  });
};

export const useRebuildReport = (businessId: string | null) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      reportId,
      description,
    }: {
      reportId: string;
      description?: string;
    }) =>
      api
        .post<{
          report: ProfitabilityReport;
        }>(
          `${reportsPath(businessId, reportId)}/build`,
          description === undefined ? {} : { description },
        )
        .then((res) => res.data.report),
    onSuccess: () => invalidateReports(client, businessId),
  });
};

export const useAnswerReport = (businessId: string | null) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      reportId,
      answers,
    }: {
      reportId: string;
      answers: string[];
    }) =>
      api
        .post<{
          report: ProfitabilityReport;
        }>(`${reportsPath(businessId, reportId)}/answer`, { answers })
        .then((res) => res.data.report),
    onSuccess: () => invalidateReports(client, businessId),
  });
};

/**
 * A template's versions, newest first. Keyed on the draft and the published
 * version, so publishing or a new draft reads the list again.
 */
export const useReportVersions = (
  businessId: string | null,
  report: Pick<
    ProfitabilityReport,
    "id" | "draft_version_id" | "published_sample_run_id"
  >,
) => {
  const api = useApi();
  return useQuery<ReportVersion[]>({
    queryKey: [
      ...reportKey(businessId),
      "reports",
      report.id,
      "versions",
      report.draft_version_id,
      report.published_sample_run_id,
    ],
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      api
        .get<{
          versions: ReportVersion[];
        }>(`${reportsPath(businessId, report.id)}/versions`, { signal })
        .then((res) => res.data.versions),
    enabled: !!businessId,
  });
};

/** Brings an earlier version back as the draft waiting for review. */
export const useRestoreVersion = (businessId: string | null) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      reportId,
      versionId,
    }: {
      reportId: string;
      versionId: string;
    }) =>
      api
        .post<{
          report: ProfitabilityReport;
        }>(`${reportsPath(businessId, reportId)}/restore`, {
          version_id: versionId,
        })
        .then((res) => res.data.report),
    onSuccess: () => invalidateReports(client, businessId),
  });
};

export const usePublishReport = (businessId: string | null) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      reportId,
      versionId,
      period,
    }: {
      reportId: string;
      versionId: string;
      /** The period a first version's sample covers, when the owner says it. */
      period?: ReportDates | null;
    }) =>
      api
        .post<{
          report: ProfitabilityReport;
        }>(`${reportsPath(businessId, reportId)}/publish`, {
          version_id: versionId,
          ...period,
        })
        .then((res) => res.data.report),
    // Publishing starts a first version's period, and the agent's page stays
    // mounted: its overview, history and Review read again, not just the list.
    onSuccess: () => {
      invalidateReports(client, businessId);
      void invalidateReportViews(client, businessId);
    },
  });
};

/**
 * The archived templates (RPT-008): kept with everything they made, hidden
 * from the list, and restorable. The query string is in the path so the
 * active list's cache never answers for it.
 */
export const useArchivedReports = (businessId: string | null) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [...reportKey(businessId), "reports", "archived"],
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      api
        .get<{
          reports: ProfitabilityReport[];
          has_more: boolean;
          next_page: number | null;
        }>(reportsPath(businessId), {
          params: { archived: true, workflow_version: 2, page: pageParam },
          signal,
        })
        .then((res) => res.data),
    getNextPageParam: (page) => (page.has_more ? page.next_page : null),
    enabled: !!businessId,
    retry: false,
  });
};

/** Archives a template, or restores one: nothing it made is removed either way. */
export const useArchiveReport = (
  businessId: string | null,
  action: "archive" | "restore" = "archive",
) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (reportId: string) =>
      api
        .post<{
          report: ProfitabilityReport;
        }>(`${reportsPath(businessId, reportId)}/${action}`)
        .then((res) => res.data.report),
    onSuccess: () => invalidateReports(client, businessId),
  });
};
